import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardMedia,
  CardActions,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  Chip,
  Tooltip,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ImageIcon from '@mui/icons-material/Image';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddCommentIcon from '@mui/icons-material/AddComment';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import FileUpload from '../Redesing/file-upload';
import CardView from '../Redesing/card-view';
import {
  getTicketImageUploadUrl,
  confirmTicketImageUpload,
  uploadImageToSignedUrl,
} from '../../api/problems';

interface TicketImage {
  id: string;
  link?: string;
  url?: string;
  category?: string;
  note?: string;
  pending?: boolean;
  isNew?: boolean;
  file?: File;
  tempUrl?: string;
  toDelete?: boolean;
}

interface TicketImageManagerProps {
  ticketId: string;
  systemId?: string;
  existingImages?: TicketImage[];
  onImagesUpdated?: (images: TicketImage[]) => void;
  readOnly?: boolean;
  viewOnly?: boolean;
}

export interface TicketImageManagerRef {
  uploadImages: (overrideTicketId?: string) => Promise<boolean>;
  getPendingImages: () => TicketImage[];
}

const TicketImageManager = forwardRef<TicketImageManagerRef, TicketImageManagerProps>(
  ({ ticketId, systemId = '', existingImages = [], onImagesUpdated, readOnly = false, viewOnly = false }, ref) => {
    const [images, setImages] = useState<TicketImage[]>(existingImages);
    const [uploading, setUploading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [editingNote, setEditingNote] = useState(false);
    const [tempNote, setTempNote] = useState('');
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Store File objects separately to prevent React state serialization issues
    const fileMapRef = useRef<Map<string, File>>(new Map());

    // Sync when existingImages prop changes (e.g., GraphQL data arrives after mount)
    useEffect(() => {
      if (existingImages.length === 0) return;
      setImages((prev) => {
        const localOnly = prev.filter((img) => img.isNew);
        return [...existingImages, ...localOnly];
      });
    }, [existingImages]);

    // Handle image drop - just store locally
    const handleImageDrop = (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      setError(null);

      const newImages = acceptedFiles.map((file) => {
        const imageId = `temp-${Date.now()}-${Math.random()}`;

        // Store the File object in ref to prevent state serialization issues
        fileMapRef.current.set(imageId, file);

        return {
          id: imageId,
          file,
          tempUrl: URL.createObjectURL(file),
          category: 'attachment',
          note: '',
          isNew: true,
          pending: true,
        };
      });

      const updatedImages = [...images, ...newImages];
      setImages(updatedImages);

      if (onImagesUpdated) {
        onImagesUpdated(updatedImages);
      }
    };

    // Update image note
    const updateImageNote = (imageId: string, note: string) => {
      const updatedImages = images.map((img) => {
        if (img.id === imageId) {
          return { ...img, note };
        }
        return img;
      });

      setImages(updatedImages);

      if (onImagesUpdated) {
        onImagesUpdated(updatedImages);
      }
    };

    // Save note for current image
    const saveCurrentImageNote = () => {
      const visibleImages = images.filter((img) => !img.toDelete);
      const currentImage = visibleImages[selectedImageIndex];
      if (currentImage) {
        updateImageNote(currentImage.id, tempNote);
        setEditingNote(false);
      }
    };

    // Upload images to server
    const uploadImages = async (overrideTicketId?: string): Promise<boolean> => {
      const imagesToUpload = images.filter((img) => img.isNew && !img.toDelete);

      if (imagesToUpload.length === 0) {
        return true;
      }

      // Use override ticket ID if provided (for new tickets)
      const actualTicketId = overrideTicketId || ticketId;

      if (!actualTicketId) {
        console.error('No ticket ID provided for image upload');
        setError('Cannot upload images without a ticket ID');
        return false;
      }

      setUploading(true);
      setError(null);

      try {
        const uploadPromises = imagesToUpload.map(async (image) => {
          // Get the actual File object from the ref
          const file = fileMapRef.current.get(image.id);

          if (!file) {
            return null;
          }

          // Step 1: Get signed upload URL
          const uploadData = await getTicketImageUploadUrl(
            actualTicketId,
            'attachment',
            systemId
          );

          if (!uploadData || !uploadData.upload_url) {
            throw new Error('Failed to get upload URL');
          }

          // Step 2: Upload image to signed URL using file from ref
          await uploadImageToSignedUrl(uploadData.upload_url, file);

          // Step 3: Confirm upload with note
          await confirmTicketImageUpload(
            actualTicketId,
            uploadData.image_id,
            image.note || '',
            systemId
          );

          return {
            ...image,
            id: uploadData.image_id,
            link: uploadData.image_url,
            isNew: false,
            pending: false,
            file: undefined,
            tempUrl: undefined,
          };
        });

        const uploadedImages = await Promise.all(uploadPromises);
        const successfulUploads = uploadedImages.filter(Boolean);

        // Update images with uploaded versions
        const updatedImages = images.map((img) => {
          if (img.isNew) {
            const uploaded = successfulUploads.find(
              (u) => u?.tempUrl === img.tempUrl
            );
            return uploaded || img;
          }
          return img;
        }).filter(img => !img.toDelete);

        setImages(updatedImages);

        if (onImagesUpdated) {
          onImagesUpdated(updatedImages);
        }

        return true;
      } catch (error) {
        console.error('Image upload error:', error);
        setError('Failed to upload images. Please try again.');
        return false;
      } finally {
        setUploading(false);
      }
    };

    // Get pending images
    const getPendingImages = () => {
      return images.filter((img) => img.isNew || img.toDelete);
    };

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      uploadImages,
      getPendingImages,
    }));

    const handleImageClick = (index: number) => {
      setSelectedImageIndex(index);
      setOpenModal(true);
      setEditingNote(false);
      const visibleImages = images.filter((img) => !img.toDelete);
      setTempNote(visibleImages[index]?.note || '');
    };

    const handleCloseModal = () => {
      setOpenModal(false);
      setEditingNote(false);
      setTempNote('');
    };

    const handlePrevImage = useCallback(() => {
      const visibleImgs = images.filter((img) => !img.toDelete);
      setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : visibleImgs.length - 1));
    }, [images]);

    const handleNextImage = useCallback(() => {
      const visibleImgs = images.filter((img) => !img.toDelete);
      setSelectedImageIndex((prev) => (prev < visibleImgs.length - 1 ? prev + 1 : 0));
    }, [images]);

    // Keyboard navigation
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (!openModal) return;

        if (e.key === 'ArrowLeft') {
          handlePrevImage();
        } else if (e.key === 'ArrowRight') {
          handleNextImage();
        } else if (e.key === 'Escape') {
          handleCloseModal();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [openModal, handlePrevImage, handleNextImage]);

    const handleDeleteImage = (imageId: string) => {
      const updatedImages = images.map((img) => {
        if (img.id === imageId) {
          if (img.isNew) {
            // Remove from file map
            fileMapRef.current.delete(imageId);
            // If it's a new image that hasn't been uploaded, remove it completely
            return null;
          }
          // Mark existing images for deletion
          return { ...img, toDelete: true };
        }
        return img;
      }).filter(Boolean) as TicketImage[];

      setImages(updatedImages);

      if (onImagesUpdated) {
        onImagesUpdated(updatedImages);
      }
    };

    const getImageUrl = (image: TicketImage): string => {
      if (image.tempUrl) return image.tempUrl;
      return image.link || image.url || '';
    };

    const getCategoryColor = (category?: string) => {
      if (!category) return 'default';
      const categoryLower = category.toLowerCase();
      if (categoryLower.includes('damage')) return 'error';
      if (categoryLower.includes('inspection')) return 'warning';
      if (categoryLower.includes('before') || categoryLower.includes('start')) return 'info';
      if (categoryLower.includes('after') || categoryLower.includes('end')) return 'success';
      return 'primary';
    };

    const visibleImages = images.filter((img) => !img.toDelete);
    const selectedImage = visibleImages[selectedImageIndex];

    // Scroll functions for horizontal gallery
    const scrollLeft = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({
          left: -300,
          behavior: 'smooth',
        });
      }
    };

    const scrollRight = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({
          left: 300,
          behavior: 'smooth',
        });
      }
    };

    // Handle upload and save for viewOnly mode
    const handleSaveImages = async () => {
      const success = await uploadImages();
      if (success) {
        setShowUploadDialog(false);
      }
    };

    // View-only mode for ticket details - but allow adding images
    if (viewOnly) {
      // If in "add mode", show the full edit interface
      if (showUploadDialog) {
        return (
          <CardView
            title="Add Ticket Images"
            headerActions={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setShowUploadDialog(false);
                    // Remove any pending images
                    const existingOnly = images.filter(img => !img.isNew);
                    setImages(existingOnly);
                  }}
                  disabled={uploading}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveImages}
                  disabled={uploading || images.filter(img => img.isNew).length === 0}
                >
                  Save
                </Button>
              </Box>
            }
            content={
              <Stack spacing={2} sx={{ p: 2 }}>
                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {uploading ? (
                  <Box display="flex" alignItems="center" justifyContent="center" p={4}>
                    <CircularProgress />
                    <Typography ml={2}>Uploading images...</Typography>
                  </Box>
                ) : (
                  <>
                    <FileUpload
                      onDrop={handleImageDrop}
                      caption="Upload images (PNG, JPG, JPEG). Max 5MB per image. You can add notes before saving."
                      accept="image/png,image/jpeg,image/jpg"
                      maxSize={5000000}
                      capture="environment"
                    />

                    {/* Show pending images with ability to add notes */}
                    {images.filter(img => img.isNew).length > 0 && (
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          ref={scrollContainerRef}
                          sx={{
                            display: 'flex',
                            gap: 2,
                            overflowX: 'auto',
                            scrollBehavior: 'smooth',
                            pb: 1,
                            '&::-webkit-scrollbar': { height: '6px' },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'grey.200',
                              borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'grey.400',
                              borderRadius: '3px',
                            },
                          }}
                        >
                          {images.filter(img => img.isNew).map((image, index) => (
                            <Card key={image.id} sx={{ flexShrink: 0, width: '320px' }}>
                              <CardMedia
                                component="img"
                                height="200"
                                image={getImageUrl(image)}
                                alt={`Image ${index + 1}`}
                                sx={{ objectFit: 'cover', cursor: 'pointer', opacity: image.pending ? 0.7 : 1 }}
                                onClick={() => handleImageClick(images.indexOf(image))}
                              />
                              <CardActions sx={{ justifyContent: 'space-between' }}>
                                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                  <Stack spacing={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                      {image.category || 'attachment'}
                                    </Typography>
                                    <Typography variant="caption" color="info.main" display="block">
                                      Will be uploaded on save
                                    </Typography>
                                    {image.note ? (
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        noWrap
                                        title={image.note}
                                        sx={{ backgroundColor: 'action.hover', px: 0.5, borderRadius: 0.5 }}
                                      >
                                        📝 {image.note}
                                      </Typography>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>
                                        Click note icon to add
                                      </Typography>
                                    )}
                                  </Stack>
                                </Box>
                                <Stack direction="row">
                                  <Tooltip title={image.note ? "Edit note" : "Add note"}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleImageClick(images.indexOf(image));
                                        setEditingNote(true);
                                        setTempNote(image.note || '');
                                      }}
                                      sx={{ color: image.note ? 'primary.main' : 'action.active' }}
                                    >
                                      {image.note ? <EditIcon fontSize="small" /> : <NoteAddIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleImageClick(images.indexOf(image));
                                    }}
                                    title="View fullscreen"
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteImage(image.id);
                                    }}
                                    title="Delete"
                                    sx={{ color: 'error.main' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </CardActions>
                            </Card>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            }
          />
        );
      }

      // Default view-only display
      return (
        <>
          <CardView
            title="Ticket Images"
            headerActions={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {visibleImages.length > 0 && (
                  <Chip
                    label={`${visibleImages.length} ${visibleImages.length === 1 ? 'image' : 'images'}`}
                    size="small"
                    variant="outlined"
                  />
                )}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddPhotoAlternateIcon />}
                  onClick={() => setShowUploadDialog(true)}
                >
                  Add
                </Button>
              </Box>
            }
            content={
              <Stack spacing={2} sx={{ p: 2 }}>
                {(!visibleImages || visibleImages.length === 0) && (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No images available for this ticket
                    </Typography>
                  </Box>
                )}

                {/* Horizontal scrollable gallery for view-only mode */}
                <Box sx={{ position: 'relative' }}>
                  {/* Left scroll button */}
                  {visibleImages.length > (isMobile ? 1 : 3) && (
                    <IconButton
                      onClick={scrollLeft}
                      sx={{
                        position: 'absolute',
                        left: -16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                      size="small"
                    >
                      <NavigateBeforeIcon fontSize="small" />
                    </IconButton>
                  )}

                  {/* Right scroll button */}
                  {visibleImages.length > (isMobile ? 1 : 3) && (
                    <IconButton
                      onClick={scrollRight}
                      sx={{
                        position: 'absolute',
                        right: -16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                      size="small"
                    >
                      <NavigateNextIcon fontSize="small" />
                    </IconButton>
                  )}

                  {/* Scrollable container */}
                  <Box
                    ref={scrollContainerRef}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      overflowX: 'auto',
                      scrollBehavior: 'smooth',
                      pb: 1,
                      '&::-webkit-scrollbar': {
                        height: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'grey.200',
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'grey.400',
                        borderRadius: '3px',
                        '&:hover': {
                          backgroundColor: 'grey.500',
                        },
                      },
                    }}
                  >
                    {visibleImages.map((image, index) => (
                      <Box
                        key={image.id}
                        sx={{
                          position: 'relative',
                          flexShrink: 0,
                          width: isMobile ? '100px' : '150px',
                          height: '150px',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            '& .image-overlay': {
                              opacity: 1,
                            },
                          },
                        }}
                        onClick={() => handleImageClick(index)}
                      >
                        <img
                          src={getImageUrl(image)}
                          alt={image.category || `Image ${index + 1}`}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />

                        {/* Overlay with category and info */}
                        <Box
                          className="image-overlay"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                            p: 1.5,
                            opacity: 0.9,
                            transition: 'opacity 0.3s ease',
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            {image.category && (
                              <Chip
                                label={image.category}
                                size="small"
                                color={getCategoryColor(image.category) as any}
                                sx={{ height: 22 }}
                              />
                            )}
                            <Stack direction="row" spacing={0.5}>
                              {image.note && (
                                <Tooltip title="Has note">
                                  <NoteAddIcon sx={{ color: 'white', fontSize: 18 }} />
                                </Tooltip>
                              )}
                              <Tooltip title="View fullscreen">
                                <FullscreenIcon sx={{ color: 'white', fontSize: 18 }} />
                              </Tooltip>
                            </Stack>
                          </Stack>
                          {image.note && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'white',
                                display: 'block',
                                mt: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {image.note}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Stack>
            }
          />

          {/* Fullscreen Modal */}
          <Dialog
            open={openModal}
            onClose={handleCloseModal}
            maxWidth={false}
            fullWidth
            PaperProps={{
              sx: {
                bgcolor: 'grey.900',
                height: '100vh',
                width: '100vw',
                m: 0,
                maxWidth: '100vw',
                maxHeight: '100vh',
                borderRadius: 0,
              },
            }}
          >
            <Box sx={{ position: 'relative', height: '100vh', width: '100vw', overflow: 'hidden' }}>
              {/* Header with close button and counter */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  zIndex: 10,
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                }}
              >
                <Chip
                  label={`${selectedImageIndex + 1} / ${visibleImages.length}`}
                  sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
                />
                <IconButton
                  onClick={handleCloseModal}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Navigation arrows */}
              {visibleImages.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.7)',
                      },
                    }}
                  >
                    <NavigateBeforeIcon fontSize="large" />
                  </IconButton>

                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.7)',
                      },
                    }}
                  >
                    <NavigateNextIcon fontSize="large" />
                  </IconButton>
                </>
              )}

              {/* Image display - fills entire viewport */}
              {selectedImage && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'grey.900',
                    p: 2,
                  }}
                >
                  <img
                    src={getImageUrl(selectedImage)}
                    alt={selectedImage.category || 'Ticket image'}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </Box>
              )}

              {/* Category/note info at bottom */}
              {selectedImage && (selectedImage.category || selectedImage.note) && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 2,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                    zIndex: 10,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    {selectedImage.category && (
                      <Chip
                        label={selectedImage.category}
                        size="small"
                        color={getCategoryColor(selectedImage.category) as any}
                      />
                    )}
                    {selectedImage.note && (
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        Note: {selectedImage.note}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              {/* Thumbnail strip for navigation in view-only mode */}
              {visibleImages.length > 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    gap: 0.5,
                    p: 0.5,
                    bgcolor: 'rgba(0,0,0,0.8)',
                    justifyContent: 'center',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    '&::-webkit-scrollbar': {
                      height: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'grey.800',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'grey.600',
                    },
                  }}
                >
                  {visibleImages.map((image, index) => (
                    <Box
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      sx={{
                        flexShrink: 0,
                        width: 60,
                        height: 45,
                        borderRadius: 0.5,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: selectedImageIndex === index ? 2 : 0,
                        borderColor: 'primary.main',
                        opacity: selectedImageIndex === index ? 1 : 0.6,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                    >
                      <img
                        src={getImageUrl(image)}
                        alt={`Thumbnail ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Dialog>
        </>
      );
    }

    return (
      <>
        <CardView
          title="Ticket Images"
          content={
            <Stack spacing={2} sx={{ p: 2 }}>
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {!readOnly && (
                <Box>
                  {uploading ? (
                    <Box display="flex" alignItems="center" justifyContent="center" p={4}>
                      <CircularProgress />
                      <Typography ml={2}>Uploading images...</Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      <FileUpload
                        onDrop={handleImageDrop}
                        caption="Upload images (PNG, JPG, JPEG). Max 5MB per image. Images will be uploaded when you save the ticket."
                        accept="image/png,image/jpeg,image/jpg"
                        maxSize={5000000}
                        capture="environment"
                      />
                      <Alert severity="info" icon={<AddCommentIcon />}>
                        <Typography variant="body2">
                          <strong>Tip:</strong> You can add descriptive notes to new images before uploading. Notes cannot be edited after upload.
                        </Typography>
                      </Alert>
                    </Stack>
                  )}
                </Box>
              )}

              {visibleImages.length > 0 ? (
            <Box sx={{ position: 'relative' }}>
              {/* Left scroll button */}
              {visibleImages.length > (isMobile ? 1 : 3) && (
                <IconButton
                  onClick={scrollLeft}
                  sx={{
                    position: 'absolute',
                    left: -16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  size="small"
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
              )}

              {/* Right scroll button */}
              {visibleImages.length > (isMobile ? 1 : 3) && (
                <IconButton
                  onClick={scrollRight}
                  sx={{
                    position: 'absolute',
                    right: -16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  size="small"
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              )}

              {/* Horizontal scrollable container */}
              <Box
                ref={scrollContainerRef}
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  scrollBehavior: 'smooth',
                  pb: 1,
                  // Hide scrollbar but keep functionality
                  '&::-webkit-scrollbar': {
                    height: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.200',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.400',
                    borderRadius: '3px',
                    '&:hover': {
                      backgroundColor: 'grey.500',
                    },
                  },
                }}
              >
                {visibleImages.map((image, index) => (
                  <Card
                    key={image.id}
                    sx={{
                      flexShrink: 0,
                      width: isMobile ? '280px' : '320px',
                    }}
                    >
                    <CardMedia
                      component="img"
                      height={isMobile ? "180" : "200"}
                      image={getImageUrl(image)}
                      alt={`Image ${index + 1}`}
                      sx={{
                        objectFit: 'cover',
                        cursor: 'pointer',
                        opacity: image.pending ? 0.7 : 1,
                      }}
                      onClick={() => handleImageClick(index)}
                    />
                    <CardActions sx={{ justifyContent: 'space-between' }}>
                      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            {image.category || 'attachment'}
                          </Typography>
                          {image.isNew && (
                            <Typography variant="caption" color="info.main" display="block">
                              Will be uploaded on save
                            </Typography>
                          )}
                          {image.note ? (
                            <Typography
                              variant="caption"
                              display="block"
                              noWrap
                              title={image.note}
                              sx={{
                                backgroundColor: 'action.hover',
                                px: 0.5,
                                borderRadius: 0.5,
                              }}
                            >
                              📝 {image.note}
                            </Typography>
                          ) : !readOnly && image.isNew && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ fontStyle: 'italic' }}
                            >
                              Click note icon to add
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                      <Stack direction="row">
                        {!readOnly && image.isNew && (
                          <Tooltip title={image.note ? "Edit note" : "Add note"}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleImageClick(index);
                                setEditingNote(true);
                                setTempNote(image.note || '');
                              }}
                              sx={{
                                color: image.note ? 'primary.main' : 'action.active',
                              }}
                            >
                              {image.note ? <EditIcon fontSize="small" /> : <NoteAddIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick(index);
                          }}
                          title="View fullscreen"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {!readOnly && image.isNew && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(image.id);
                            }}
                            title="Delete"
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No images selected
                </Typography>
              )}
            </Stack>
          }
        />

        {/* Fullscreen Modal for Edit Mode */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth={false}
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'grey.900',
              height: '100vh',
              width: '100vw',
              m: 0,
              maxWidth: '100vw',
              maxHeight: '100vh',
              borderRadius: 0,
            },
          }}
        >
          <Box sx={{ position: 'relative', height: '100vh', width: '100vw', overflow: 'hidden' }}>
            {/* Header with close button and counter */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                zIndex: 10,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={`${selectedImageIndex + 1} / ${visibleImages.length}`}
                  sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
                />
                {!readOnly && selectedImage?.isNew && !selectedImage?.note && !editingNote && (
                  <Chip
                    icon={<NoteAddIcon />}
                    label="Add Note"
                    onClick={() => {
                      setEditingNote(true);
                      setTempNote('');
                    }}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'white',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.2)',
                      },
                    }}
                  />
                )}
              </Stack>
              <IconButton
                onClick={handleCloseModal}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.7)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Navigation arrows */}
            {visibleImages.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrevImage}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <NavigateBeforeIcon fontSize="large" />
                </IconButton>

                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <NavigateNextIcon fontSize="large" />
                </IconButton>
              </>
            )}

            {/* Image display - fills entire viewport */}
            {selectedImage && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: 'grey.900',
                  p: 2,
                }}
              >
                <img
                  src={getImageUrl(selectedImage)}
                  alt={selectedImage.category || 'Ticket image'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    console.error('Failed to load image:', target.src);
                  }}
                />
              </Box>
            )}

            {/* Info overlay at bottom */}
            {selectedImage && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: 2,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)',
                  zIndex: 10,
                }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {selectedImage.category && (
                      <Chip
                        label={selectedImage.category}
                        size="small"
                        color={getCategoryColor(selectedImage.category) as any}
                      />
                    )}
                    {selectedImage.isNew && (
                      <Chip
                        label="Pending upload"
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ borderColor: 'white', color: 'white' }}
                      />
                    )}
                  </Stack>

                  {/* Note section - only editable for new images */}
                  {!readOnly && selectedImage?.isNew && (
                    <Box>
                      {editingNote ? (
                        <Stack spacing={1}>
                          <Typography variant="caption">
                            Add a note to describe this image:
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                              value={tempNote}
                              onChange={(e) => setTempNote(e.target.value)}
                              placeholder="Add note here..."
                              size="small"
                              fullWidth
                              variant="outlined"
                              autoFocus
                              multiline
                              maxRows={3}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  saveCurrentImageNote();
                                }
                              }}
                            />
                            <Stack>
                              <IconButton
                                size="small"
                                onClick={saveCurrentImageNote}
                                sx={{
                                  bgcolor: 'success.main',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: 'success.dark',
                                  },
                                }}
                                title="Save note"
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingNote(false);
                                  setTempNote(selectedImage.note || '');
                                }}
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                  },
                                }}
                                title="Cancel"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                            Press Enter to save, Shift+Enter for new line
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                          {selectedImage.note ? (
                            <>
                              <Box sx={{
                                flex: 1,
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                borderRadius: 1,
                                p: 1,
                              }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                  Note:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'white' }}>
                                  {selectedImage.note}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingNote(true);
                                  setTempNote(selectedImage.note || '');
                                }}
                                sx={{
                                  color: 'white',
                                  bgcolor: 'rgba(255,255,255,0.1)',
                                  '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                  },
                                }}
                                title="Edit note"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<AddCommentIcon />}
                              onClick={() => {
                                setEditingNote(true);
                                setTempNote('');
                              }}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.15)',
                                color: 'white',
                                '&:hover': {
                                  bgcolor: 'rgba(255,255,255,0.25)',
                                },
                              }}
                            >
                              Add Note to This Image
                            </Button>
                          )}
                        </Stack>
                      )}
                    </Box>
                  )}

                  {/* Note display for existing images or view-only mode */}
                  {(readOnly || !selectedImage?.isNew) && selectedImage?.note && (
                    <Box sx={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderRadius: 1,
                      p: 1,
                    }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Note:
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {selectedImage.note}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

            {/* Thumbnail strip for navigation */}
            {visibleImages.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  gap: 0.5,
                  p: 0.5,
                  bgcolor: 'rgba(0,0,0,0.8)',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  '&::-webkit-scrollbar': {
                    height: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.800',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.600',
                  },
                }}
              >
                {visibleImages.map((image, index) => (
                  <Box
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    sx={{
                      flexShrink: 0,
                      width: 60,
                      height: 45,
                      borderRadius: 0.5,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: selectedImageIndex === index ? 2 : 0,
                      borderColor: 'primary.main',
                      opacity: selectedImageIndex === index ? 1 : 0.6,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        opacity: 1,
                      },
                    }}
                  >
                    <img
                      src={getImageUrl(image)}
                      alt={`Thumbnail ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Dialog>
      </>
    );
  }
);

TicketImageManager.displayName = 'TicketImageManager';

export default TicketImageManager;